// @ts-nocheck
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import ApplicationBody from '@/components/ApplicationBody';
import PDCPageComponent from '@/components/PDCPageComponent';
import SystemMaintenancePage from '@/components/SystemMaintenancePage';
import TextFragmentComponent from '@/components/TextFragmentComponent';
import { DEFAULT_LOCALE, ROUTE_PATH_START } from '@/constants';
import { getLocale } from '@/features/login/selector';
import generateActiveMessages from '@/lib/generateActiveMessages';
import { isUnderMaintenance } from '@/lib/systemMaintenance';
import { getUIState, getVehicleConditionFilter } from '@/selectors';

const EMPTY_ITEMS = [];

export const UnconnectedPDCContainer = ({
    activeMessages,
    locale,
    showSessionExpired,
    pdc,
    pdcItemsEmpty,
    filters,
    firstLoad,
}) => {
    // Checked before the "no inspections" redirect below, otherwise the empty
    // result caused by the downtime would bounce the user to the start page.
    if (isUnderMaintenance()) {
        return (
            <ApplicationBody locale={locale} activeMessages={activeMessages} showSessionExpired={showSessionExpired}>
                <IntlProvider
                    defaultLocale={DEFAULT_LOCALE}
                    locale={locale}
                    messages={activeMessages}
                    textComponent={TextFragmentComponent}
                >
                    <SystemMaintenancePage />
                </IntlProvider>
            </ApplicationBody>
        );
    }

    if (firstLoad && pdcItemsEmpty) {
        return <Navigate to={ROUTE_PATH_START} />;
    }

    return (
        <ApplicationBody locale={locale} activeMessages={activeMessages} showSessionExpired={showSessionExpired}>
            <IntlProvider
                defaultLocale={DEFAULT_LOCALE}
                locale={locale}
                messages={activeMessages}
                textComponent={TextFragmentComponent}
            >
                <PDCPageComponent pdc={pdc} filters={filters} />
            </IntlProvider>
        </ApplicationBody>
    );
};

UnconnectedPDCContainer.propTypes = {
    activeMessages: PropTypes.object.isRequired,
    locale: PropTypes.string.isRequired,
    showSessionExpired: PropTypes.bool,
    pdc: PropTypes.shape(
        PropTypes.objectOf({
            items: PropTypes.array.isRequired,
            links: PropTypes.object.isRequired,
        })
    ).isRequired,
    pdcItemsEmpty: PropTypes.bool.isRequired,
    filters: PropTypes.array.isRequired,
    allFilters: PropTypes.array.isRequired,
    firstLoad: PropTypes.any,
};

const PDCContainer = ({ translations }) => {
    // Select only the slices this container needs, so unrelated store changes do not re-render the page
    const locale = useSelector(getLocale);
    const userSessionExpired = useSelector(state => state.login.userSessionExpired);
    const sessionExpiredAcknowledged = useSelector(state => getUIState(state)?.sessionExpiredAcknowledged);
    const firstLoad = useSelector(state => get(state, 'pdc.firstLoad'));
    const items = useSelector(state => get(state, 'pdc.items', EMPTY_ITEMS));
    const links = useSelector(state => get(state, 'pdc.links'));
    const filters = useSelector(getVehicleConditionFilter, shallowEqual);
    const props = {
        activeMessages: generateActiveMessages(locale, translations),
        locale,
        showSessionExpired: userSessionExpired && !sessionExpiredAcknowledged,
        pdc: { links, items },
        filters,
        firstLoad,
        pdcItemsEmpty: !items.length,
    };
    return <UnconnectedPDCContainer {...props} />;
};

export default PDCContainer;
